import { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Réglages du site, rubriques et configuration de page : une seule requête
 * pour toute la page.
 *
 * Trois composants demandaient `site-settings` au même instant (Layout pour la
 * typographie, Header pour le nom du site, Footer pour les réseaux — plus
 * ArticleCard pour la mise en page du héro), deux demandaient `categories` et
 * deux `page-config`. Six allers-retours pour trois réponses identiques. Le
 * motif est celui, déjà éprouvé, de `useContentControls` : un cache de module
 * et une requête en vol partagée.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function makeResource(path, fallback) {
  let cached = null;
  let inflight = null;

  const load = () => {
    if (cached !== null) return Promise.resolve(cached);
    if (!inflight) {
      inflight = axios.get(`${API_URL}${path}`)
        .then((res) => { cached = res.data ?? fallback; return cached; })
        .catch(() => fallback)
        .finally(() => { inflight = null; });
    }
    return inflight;
  };

  load.invalidate = () => { cached = null; inflight = null; };
  load.peek = () => cached;
  return load;
}

export const fetchSiteSettings = makeResource('/api/site-settings', {});
export const fetchCategories = makeResource('/api/categories', []);
export const fetchPageConfig = makeResource('/api/page-config', null);

function useResource(resource, initial) {
  const [data, setData] = useState(() => resource.peek() ?? initial);
  useEffect(() => {
    let alive = true;
    resource().then((value) => { if (alive) setData(value); });
    return () => { alive = false; };
  }, [resource]);
  return data;
}

export function useSiteSettings() { return useResource(fetchSiteSettings, null); }
export function useCategories() { return useResource(fetchCategories, []); }
